import { useEffect } from 'react'

import { useAuthToken } from '@/hooks/useAuthToken'
import { getUserTimezone, isValidTimeZone } from '@/lib/timezone'
import { meService, StampTimeZoneError } from '@/services/meService'
import type { User } from '@/types/user'

// Claimed up front so Strict Mode's double-mount shares one in-flight
// request. Released on a retryable failure (no token, transport, 5xx) so a
// later /users/me revalidation can try again. A 4xx stays claimed: the
// backend already no-ops a repeat write, but an invalid IANA zone will not
// become valid later in the session.
const attemptedUserIds = new Set<string>()

// A zone the browser cannot read counts as none; the backend replaces it too
// (ENG-4078).
function hasStoredTimeZone(user: User): boolean {
  const zone = user.learner_info?.time_zone
  return Boolean(zone) && isValidTimeZone(zone as string)
}

function isClientStampError(error: unknown): boolean {
  return error instanceof StampTimeZoneError && error.status >= 400 && error.status < 500
}

/**
 * Stamp the browser IANA zone onto the signed-in user (ENG-3700).
 *
 * Fires PUT /me/time-zone from DatabaseUserGuard. Skips when the resolved
 * user already has a zone. Never blocks render and never surfaces an error:
 * a failure here is invisible by design. Impersonated sessions are a no-op
 * on the backend, so this hook does not special-case them.
 */
export function useStampLearnerTimeZone(user: User | undefined): void {
  const { getToken } = useAuthToken()

  useEffect(() => {
    if (!user || hasStoredTimeZone(user) || attemptedUserIds.has(user.id)) {
      return
    }
    attemptedUserIds.add(user.id)

    void (async () => {
      try {
        const token = await getToken()
        if (!token) {
          attemptedUserIds.delete(user.id)
          return
        }
        await meService.stampTimeZone(token, getUserTimezone())
      } catch (error) {
        if (!isClientStampError(error)) {
          attemptedUserIds.delete(user.id)
        }
      }
    })()
  }, [user, getToken])
}

/** Test-only: drop session state so cases don't leak. */
export function resetStampedLearnerTimeZones(): void {
  attemptedUserIds.clear()
}
